import { FC } from 'react';

import { ArrowNarrowDirection, ArrowNarrowIcon } from '../../../../assets/icons/arrow-narrow-icon';
import { DownloadFileIcon, XCloseIcon } from '../../../../assets/icons/index';
import MinusIcon from '../../../../assets/icons/minus-icon';
import PlusIcon from '../../../../assets/icons/plus-icon';
import { canZoomIn, canZoomOut } from './zoom.const';

const ICON_COLOUR = '#FFFFFF';

const HeaderButton: FC<{
  children: React.ReactNode;
  isDisabled?: boolean;
  label: string;
  onClick: () => void;
}> = ({ children, isDisabled = false, label, onClick }) => (
  <button
    aria-label={label}
    className="flex size-6 shrink-0 items-center justify-center disabled:opacity-40"
    disabled={isDisabled}
    onClick={onClick}
    type="button"
  >
    {children}
  </button>
);

// The dark pill the design sets a number in: the page the reader is on, and the zoom.
const Readout: FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className = 'size-6',
}) => (
  <span
    className={`flex items-center justify-center rounded-[2px] bg-gray-800 px-[7px] text-label-sm font-medium text-white ${className}`}
  >
    {children}
  </span>
);

/**
 * The bar across the top of the viewer — the design's `PDF Header`.
 *
 * Three groups on one row: what is open on the left, where in it the reader is in the middle, and
 * what they can do with it on the right. The page count and the zoom are the same pill in the
 * design, so they are the same component here.
 *
 * Download is absent rather than disabled for a viewer-only recipient: a disabled control
 * advertises a door, and 20.1.0's two grants differ precisely in whether that door exists.
 */
export const FilePreviewHeader: FC<{
  canDownload: boolean;
  fileName: string;
  onClose: () => void;
  onDownload: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  page: { current: number; total: number } | null;
  zoom: number;
}> = ({ canDownload, fileName, onClose, onDownload, onZoomIn, onZoomOut, page, zoom }) => (
  <div className="flex h-16 shrink-0 items-center gap-6 bg-gray-700 p-4 shadow-[0px_4px_6px_-2px_rgba(16,24,40,0.03),0px_12px_16px_-4px_rgba(16,24,40,0.08)]">
    <div className="flex min-w-0 flex-1 items-center gap-4">
      <HeaderButton label="Close preview" onClick={onClose}>
        <ArrowNarrowIcon
          className="size-6 rotate-180"
          direction={ArrowNarrowDirection.RIGHT}
          iconColor={ICON_COLOUR}
        />
      </HeaderButton>
      <p className="truncate text-base font-semibold leading-8 text-white">{fileName}</p>
    </div>

    <div className="flex shrink-0 items-start gap-2">
      {page && (
        <>
          <div className="flex items-center gap-2 px-6">
            <Readout>{page.current}</Readout>
            <span className="text-sm font-medium text-white">/</span>
            <span className="text-sm font-medium text-white">{page.total}</span>
          </div>
          <span className="h-6 w-px bg-gray-500" />
        </>
      )}
      <div className="flex items-center gap-4 px-6">
        <HeaderButton isDisabled={!canZoomOut(zoom)} label="Zoom out" onClick={onZoomOut}>
          <MinusIcon className="size-6" iconColor={ICON_COLOUR} />
        </HeaderButton>
        <Readout className="h-6">{zoom}%</Readout>
        <HeaderButton isDisabled={!canZoomIn(zoom)} label="Zoom in" onClick={onZoomIn}>
          <PlusIcon className="size-6" iconColor={ICON_COLOUR} />
        </HeaderButton>
      </div>
    </div>

    <div className="flex flex-1 items-center justify-end gap-4">
      {canDownload && (
        <HeaderButton label="Download" onClick={onDownload}>
          <DownloadFileIcon className="size-6" iconColor={ICON_COLOUR} />
        </HeaderButton>
      )}
      <HeaderButton label="Close" onClick={onClose}>
        <XCloseIcon className="size-6" iconColor={ICON_COLOUR} />
      </HeaderButton>
    </div>
  </div>
);
